import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LiveChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isStarted, setIsStarted] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  // Load session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem("chat_session");
    if (savedSession) {
      try {
        const { sessionId: savedId, userName: savedName, userEmail: savedEmail } = JSON.parse(savedSession);
        if (savedId) {
          // Check if session is still active
          checkSessionStatus(savedId, savedName, savedEmail);
        }
      } catch (e) {
        console.error("Error loading chat session:", e);
        localStorage.removeItem("chat_session");
      }
    }
  }, []);

  // Check if session is still active
  const checkSessionStatus = async (sid, savedName, savedEmail) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${sid}`);
      if (response.data.status === 'closed') {
        // Chat was closed - show closed state with message history
        setSessionId(sid);
        setUserName(savedName || "");
        setUserEmail(savedEmail || "");
        setIsStarted(true);
        setIsClosed(true);
        setMessages(response.data.messages || []);
      } else {
        // Chat is still active
        setSessionId(sid);
        setUserName(savedName || "");
        setUserEmail(savedEmail || "");
        setIsStarted(true);
        setIsClosed(false);
        setMessages(response.data.messages || []);
      }
    } catch (error) {
      // Session not found, clear it
      localStorage.removeItem("chat_session");
      setSessionId(null);
      setIsStarted(false);
      setIsClosed(false);
    }
  };

  // Save session when sessionId changes
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem("chat_session", JSON.stringify({
        sessionId,
        userName,
        userEmail
      }));
    }
  }, [sessionId, userName, userEmail]);

  // Fetch messages from server
  const fetchMessages = async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${sid}`);
      if (response.data.messages) {
        setMessages(response.data.messages);
      }
      // Check if chat was closed
      if (response.data.status === 'closed') {
        setIsClosed(true);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  // Poll for new messages every 2 seconds
  useEffect(() => {
    if (isStarted && sessionId && !isClosed) {
      // Initial fetch
      fetchMessages(sessionId);
      
      // Start polling
      pollInterval.current = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/chat/messages/${sessionId}`);
          if (response.data.messages) {
            const serverMessages = response.data.messages;
            
            // Check if chat was closed
            if (response.data.status === 'closed') {
              setIsClosed(true);
              clearInterval(pollInterval.current);
              return;
            }
            
            // Check for new operator messages
            setMessages(prevMessages => {
              if (serverMessages.length > prevMessages.length) {
                const newOnes = serverMessages.slice(prevMessages.length);
                const operatorMsgs = newOnes.filter(m => m.sender === 'operator');
                if (operatorMsgs.length > 0 && !isOpen) {
                  setUnreadCount(prev => prev + operatorMsgs.length);
                }
              }
              return serverMessages;
            });
          }
        } catch (error) {
          console.error("Error polling messages:", error);
        }
      }, 2000);
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [isStarted, sessionId, isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const startChat = async () => {
    if (!userName.trim()) return;

    try {
      const response = await axios.post(`${API}/chat/start`, {
        user_name: userName.trim(),
        user_email: userEmail.trim() || null
      });

      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      setIsStarted(true);
      
      // Add welcome message locally
      const welcomeMsg = {
        id: "welcome",
        text: `Здравствуйте, ${userName}! Добро пожаловать в SKINKOSH. Чем могу помочь?`,
        sender: "operator",
        timestamp: new Date().toISOString()
      };
      setMessages([welcomeMsg]);
      
    } catch (error) {
      console.error("Error starting chat:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !sessionId) return;

    const msg = {
      id: Date.now().toString(),
      text: newMessage.trim(),
      sender: "user",
      timestamp: new Date().toISOString()
    };

    // Add message optimistically
    setMessages(prev => [...prev, msg]);
    setNewMessage("");

    try {
      await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: msg.text,
        sender: "user"
      });
    } catch (error) {
      console.error("Error sending message:", error);
      // Remove message on error
      setMessages(prev => prev.filter(m => m.id !== msg.id));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isStarted && !isClosed) {
        sendMessage();
      } else if (!isStarted) {
        startChat();
      }
    }
  };

  const endChat = () => {
    setIsStarted(false);
    setSessionId(null);
    setMessages([]);
    setIsClosed(false);
    localStorage.removeItem("chat_session");
    setIsOpen(false);
  };

  const startNewChat = () => {
    setIsStarted(false);
    setSessionId(null);
    setMessages([]);
    setIsClosed(false);
    localStorage.removeItem("chat_session");
    // Keep userName and userEmail for convenience
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 md:w-16 md:h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full shadow-lg shadow-blue-500/40 hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110"
        data-testid="chat-open-btn"
      >
        <MessageCircle className="w-6 h-6 md:w-7 md:h-7 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
        <span className="absolute -top-10 right-0 bg-slate-800 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden md:block">
          Онлайн чат
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-[calc(100vw-32px)] md:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${isMinimized ? 'h-14' : 'h-[450px] md:h-[500px]'}`}
      data-testid="chat-window"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 md:p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-full flex items-center justify-center">
            <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white text-sm md:text-base">Онлайн поддержка</h3>
            <p className="text-xs text-white/80 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Операторы онлайн
            </p>
          </div>
        </div>
        <div className="flex gap-1 md:gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <Minimize2 className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="chat-close-btn"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {!isStarted ? (
            /* Start Form */
            <div className="p-4 md:p-6 space-y-4">
              <div className="text-center mb-4 md:mb-6">
                <h4 className="text-base md:text-lg font-semibold text-slate-800 mb-2">Начать чат</h4>
                <p className="text-xs md:text-sm text-slate-500">Введите ваше имя для начала общения</p>
              </div>
              <Input
                placeholder="Ваше имя *"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyPress={handleKeyPress}
                className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                data-testid="chat-name-input"
              />
              <Input
                placeholder="Email (необязательно)"
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                data-testid="chat-email-input"
              />
              <Button
                onClick={startChat}
                disabled={!userName.trim()}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold"
                data-testid="chat-start-btn"
              >
                Начать чат
              </Button>
            </div>
          ) : isClosed ? (
            /* Chat Closed State */
            <div className="flex flex-col h-[380px] md:h-[430px]">
              {/* Messages - scrollable history */}
              <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 bg-slate-50">
                {messages.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-3 md:px-4 py-2 rounded-2xl shadow-sm ${
                        msg.sender === 'user'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-br-md'
                          : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === 'user' ? 'text-white/70' : 'text-slate-400'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Closed Chat Notice */}
              <div className="p-4 md:p-5 border-t border-slate-200 bg-white text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-slate-400 rounded-full"></div>
                  <p className="text-sm text-slate-600 font-medium">Чат завершён оператором</p>
                </div>
                <p className="text-xs text-slate-400 mb-4">Спасибо за обращение! Если у вас есть новые вопросы, начните новый чат.</p>
                <Button
                  onClick={startNewChat}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold"
                  data-testid="chat-start-new-btn"
                >
                  Начать новый чат
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="h-[290px] md:h-[340px] overflow-y-auto p-3 md:p-4 space-y-3 bg-slate-50">
                {messages.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-3 md:px-4 py-2 rounded-2xl shadow-sm ${
                        msg.sender === 'user'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-br-md'
                          : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === 'user' ? 'text-white/70' : 'text-slate-400'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 md:p-4 border-t border-slate-200 bg-white">
                <div className="flex gap-2">
                  <Input
                    placeholder="Введите сообщение..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 text-sm"
                    data-testid="chat-message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <button 
                  onClick={endChat}
                  className="w-full text-xs text-slate-400 hover:text-red-500 mt-2 transition-colors"
                >
                  Завершить чат
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
